import axios from '@axios'
import { storePagination } from '@/mixins'

/**
 * Módulo Vuex da tela dedicada de "Horários de Funcionamento" (D27, tarefa 16.2).
 *
 * Consome os endpoints administrativos:
 *  - Geral: GET/PUT  /api/store-opening-hours/general (StoreId nulo — template Geral)
 *  - Loja:  GET/PUT  /api/stores/{storeId}/opening-hours (horário próprio da loja)
 *
 * A resolução do horário efetivo é tudo-ou-nada (próprio-ou-Geral, D27): uma loja com ao menos
 * um dia próprio ignora o Geral inteiro; uma loja sem nenhum dia próprio usa o Geral. Por isso o
 * `PUT` de loja com lista vazia remove os registros próprios e a loja volta a herdar o Geral.
 */

// Ordem dos sete dias, com o valor acompanhando o enum DayOfWeek do backend (string).
const WEEK_DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']

// Um dia sem horário definido (fechado): sem abertura/fechamento.
const getEmptyDay = dayOfWeek => ({
  dayOfWeek,
  openingTime: null,
  closingTime: null,
})

// Garante exatamente sete dias (dom → sáb) na ordem canônica, mesclando os dias vindos do
// backend com os dias vazios que faltarem para a grade ficar sempre completa. Um dia ausente
// no backend representa "fechado" (D27) e é renderizado com os campos vazios.
const buildWeek = (days = []) => {
  const byDay = new Map((days || []).map(day => [day.dayOfWeek, day]))
  return WEEK_DAYS.map(dayOfWeek => {
    const existing = byDay.get(dayOfWeek)
    if (!existing) return getEmptyDay(dayOfWeek)
    return {
      ...getEmptyDay(dayOfWeek),
      ...existing,
      dayOfWeek,
    }
  })
}

// Monta o corpo de gravação a partir dos dias editados. Só os dias com abertura E fechamento
// preenchidos são enviados (loja aberta naquele dia); os demais ficam de fora — o que, no
// escopo de loja, significa "fechado nesse dia", e uma lista vazia limpa os registros próprios
// da loja, voltando ao Geral (D27).
const toPayload = days =>
  (days || [])
    .filter(day => day.openingTime && day.closingTime)
    .map(day => ({
      dayOfWeek: day.dayOfWeek,
      openingTime: day.openingTime,
      closingTime: day.closingTime,
    }))

const getInitialState = () => ({
  // Paginação/ordenação padrão do sistema para a listagem de lojas com horário próprio.
  ...storePagination.state(),
  // Escopo em edição (D27): 'general' (StoreId nulo) ou 'store' (uma loja).
  scope: 'general',
  selectedStoreId: null,
  stores: [],
  // Grade dos sete dias em edição.
  days: buildWeek(),
  // Verdadeiro quando a loja carregada possui ao menos um dia próprio (ignora o Geral — D27).
  hasOwnHours: false,
  // Resumo do horário Geral (fixo no topo da listagem) e das lojas com horário próprio (paginadas).
  generalSummary: null,
  scopeSummary: [],
})

export default {
  namespaced: true,
  state: getInitialState(),

  getters: {
    storeOptions(state) {
      return state.stores.map(store => ({
        value: store.id,
        label: store.tradingName || store.companyName || `#${store.id}`,
      }))
    },
  },

  mutations: {
    ...storePagination.mutations,

    SET_SCOPE(state, scope) {
      state.scope = scope
    },
    SET_SELECTED_STORE(state, storeId) {
      state.selectedStoreId = storeId
    },
    SET_STORES(state, stores) {
      state.stores = stores
    },
    SET_DAYS(state, days) {
      state.days = buildWeek(days)
    },
    SET_HAS_OWN_HOURS(state, hasOwnHours) {
      state.hasOwnHours = hasOwnHours
    },
    SET_SCOPE_SUMMARY(state, summary) {
      state.scopeSummary = summary
    },
    SET_GENERAL_SUMMARY(state, general) {
      state.generalSummary = general
    },
  },

  actions: {
    ...storePagination.actions,

    setScope({ commit }, scope) {
      commit('SET_SCOPE', scope)
    },

    setSelectedStore({ commit }, storeId) {
      commit('SET_SELECTED_STORE', storeId)
    },

    async fetchStores({ commit }) {
      const { results } = (
        await axios.get('/api/stores', { params: { pageSize: 0, pageIndex: 0 } })
      ).data
      commit('SET_STORES', results)
    },

    // Carrega o resumo dos escopos para a listagem: o Geral (fixo no topo) e as lojas com horário
    // próprio PAGINADAS. O filtro por loja e a paginação vão para o servidor (padrão do sistema).
    async fetchScopeSummary({ commit, state }, { storeId = null } = {}) {
      const { data } = await axios.get('/api/store-opening-hours/summary', {
        params: {
          storeId: storeId || null,
          pageSize: state.paging.pageSize,
          pageIndex: state.paging.currentPage - 1,
        },
      })
      commit('SET_GENERAL_SUMMARY', data.general || null)
      commit('SET_SCOPE_SUMMARY', (data.stores && data.stores.results) || [])
      commit('SET_PAGING', {
        ...state.paging,
        rowCount: (data.stores && data.stores.rowCount) || 0,
        rowsInCurrentPage: (data.stores && data.stores.rowsInCurrentPage) || 0,
      })
    },

    async fetchGeneralOpeningHours({ commit }) {
      // O endpoint retorna StoreOpeningHoursDto { storeId, days: [...] }, não um array puro.
      const { data } = await axios.get('/api/store-opening-hours/general')
      commit('SET_DAYS', data?.days || [])
      commit('SET_HAS_OWN_HOURS', true)
    },

    async fetchStoreOpeningHours({ commit }, { storeId }) {
      const { data } = await axios.get(`/api/stores/${storeId}/opening-hours`)
      const days = data?.days || []
      commit('SET_DAYS', days)
      // Loja com ao menos um dia próprio ignora o Geral inteiro (tudo ou nada — D27).
      commit('SET_HAS_OWN_HOURS', days.length > 0)
    },

    async saveGeneralOpeningHours({ state }) {
      const { data } = await axios.put('/api/store-opening-hours/general', {
        days: toPayload(state.days),
      })
      return data
    },

    async saveStoreOpeningHours({ state }, { storeId }) {
      const { data } = await axios.put(`/api/stores/${storeId}/opening-hours`, {
        days: toPayload(state.days),
      })
      return data
    },

    clearOpeningHours({ commit }) {
      commit('SET_DAYS', [])
      commit('SET_HAS_OWN_HOURS', false)
    },
  },
}
